import type { GeometryKey } from '../locales/keys';

export type SimilarityMode = 'dilation' | 'intercept' | 'similar-triangles';

export interface SimilarityPreset {
  id: string;
  mode: SimilarityMode;
  labelKey: GeometryKey;
  advancedOnly: boolean;
  // dilation config
  k?: number;
  polygonSides?: number; // number of sides for regular polygon pre-image
  // intercept config
  t?: number; // parameter along sides [0,1]
  // similar-triangles config
  triAVertices?: [[number, number], [number, number], [number, number]];
  triBVertices?: [[number, number], [number, number], [number, number]];
}

export const SIMILARITY_PRESETS: readonly SimilarityPreset[] = [
  {
    id: 'midsegment-half',
    mode: 'intercept',
    labelKey: 'similarity.preset.midsegment-half',
    advancedOnly: false,
    t: 0.5,
  },
  {
    id: 'double-k-2',
    mode: 'dilation',
    labelKey: 'similarity.preset.double-k-2',
    advancedOnly: false,
    k: 2,
    polygonSides: 4,
  },
  {
    id: 'inversion-k-minus-1',
    mode: 'dilation',
    labelKey: 'similarity.preset.inversion-k-minus-1',
    advancedOnly: true,
    k: -1,
    polygonSides: 4,
  },
  {
    id: 'aa-detective',
    mode: 'similar-triangles',
    labelKey: 'similarity.preset.aa-detective',
    advancedOnly: true,
    // 30-60-90 triangle and a scaled version (scale factor 1.5)
    triAVertices: [
      [200, 350],
      [200, 200],
      [287, 350],
    ],
    triBVertices: [
      [450, 380],
      [450, 155],
      [580, 380],
    ],
  },
];

export function isValidSimilarityPreset(p: SimilarityPreset): boolean {
  if (typeof p.id !== 'string' || p.id.length === 0) return false;
  const validModes: SimilarityMode[] = ['dilation', 'intercept', 'similar-triangles'];
  if (!validModes.includes(p.mode)) return false;
  if (typeof p.labelKey !== 'string' || p.labelKey.length === 0) return false;
  if (typeof p.advancedOnly !== 'boolean') return false;
  if (p.k !== undefined && typeof p.k !== 'number') return false;
  if (p.polygonSides !== undefined) {
    if (typeof p.polygonSides !== 'number' || p.polygonSides < 3) return false;
  }
  if (p.t !== undefined) {
    if (typeof p.t !== 'number' || p.t <= 0 || p.t >= 1) return false;
  }
  if (p.triAVertices !== undefined) {
    if (!Array.isArray(p.triAVertices) || p.triAVertices.length !== 3) return false;
    for (const v of p.triAVertices) {
      if (!Array.isArray(v) || v.length !== 2) return false;
      if (typeof v[0] !== 'number' || typeof v[1] !== 'number') return false;
    }
  }
  if (p.triBVertices !== undefined) {
    if (!Array.isArray(p.triBVertices) || p.triBVertices.length !== 3) return false;
    for (const v of p.triBVertices) {
      if (!Array.isArray(v) || v.length !== 2) return false;
      if (typeof v[0] !== 'number' || typeof v[1] !== 'number') return false;
    }
  }
  return true;
}

export function validateSimilarityPresetIds(presets: readonly SimilarityPreset[]): boolean {
  const ids = new Set<string>();
  for (const preset of presets) {
    if (ids.has(preset.id)) return false;
    ids.add(preset.id);
  }
  return true;
}
